import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { FileSignature, Plus, Trash2, Download, Copy, Mail, Clock, CheckCircle2, Hourglass } from 'lucide-react';

export default function ContractsPage() {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState({ client_email: '', client_name: '', title: '', notes: '' });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const r = await api.get('/contracts');
      setContracts(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore caricamento contratti');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!file) { toast.error('Seleziona un PDF'); return; }
    if (!form.client_email) { toast.error('Email cliente obbligatoria'); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('client_email', form.client_email);
      fd.append('client_name', form.client_name);
      fd.append('title', form.title);
      fd.append('notes', form.notes);
      const r = await api.post('/contracts', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Contratto creato. Email inviata al cliente.');
      // copy link
      try { await navigator.clipboard.writeText(r.data.public_url); } catch { /* noop */ }
      setOpenCreate(false);
      setForm({ client_email: '', client_name: '', title: '', notes: '' });
      setFile(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore creazione');
    } finally { setSubmitting(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Eliminare definitivamente questo contratto?')) return;
    try { await api.delete(`/contracts/${id}`); toast.success('Eliminato'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const downloadPdf = async (c, signed) => {
    try {
      const r = await api.get(`/contracts/${c.id}/download?signed=${signed ? 'true' : 'false'}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${c.title || 'contratto'}_${signed ? 'firmato' : 'originale'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore download');
    }
  };

  const copyLink = async (c) => {
    const base = window.location.origin;
    const url = `${base}/contratto-firma/${c.signature_token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copiato negli appunti');
    } catch {
      toast.info(url);
    }
  };

  const statusBadge = (s) => {
    if (s === 'signed') return <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300"><CheckCircle2 className="w-3 h-3 mr-1" />Firmato</Badge>;
    if (s === 'expired') return <Badge variant="destructive"><Clock className="w-3 h-3 mr-1" />Scaduto</Badge>;
    return <Badge variant="outline" className="border-amber-400 text-amber-700"><Hourglass className="w-3 h-3 mr-1" />In attesa firma</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="contracts-page">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <FileSignature className="w-7 h-7 text-amber-700" /> Contratti & Firme
          </h1>
          <p className="text-sm text-slate-600 mt-1">Invia contratti al cliente via email — firma elettronica semplice con tracciamento IP/timestamp.</p>
        </div>
        <Button onClick={() => setOpenCreate(true)} data-testid="new-contract-btn">
          <Plus className="w-4 h-4 mr-1" /> Nuovo contratto
        </Button>
      </motion.div>

      {loading ? <p>Caricamento…</p> : contracts.length === 0 ? (
        <div className="bg-white p-10 rounded-lg text-center border" data-testid="empty-contracts">
          <FileSignature className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-600">Nessun contratto creato.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
              <tr>
                <th className="text-left p-3">Titolo</th>
                <th className="text-left p-3">Cliente</th>
                <th className="text-left p-3">Stato</th>
                <th className="text-left p-3">Creato</th>
                <th className="text-left p-3">Firmato</th>
                <th className="text-right p-3">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {contracts.map(c => (
                <tr key={c.id} className="border-t hover:bg-slate-50" data-testid={`contract-row-${c.id}`}>
                  <td className="p-3 font-medium">{c.title}</td>
                  <td className="p-3">
                    <div>{c.client_name || '—'}</div>
                    <div className="text-xs text-slate-500">{c.client_email}</div>
                  </td>
                  <td className="p-3">{statusBadge(c.status)}</td>
                  <td className="p-3 text-xs text-slate-500">{new Date(c.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })}</td>
                  <td className="p-3 text-xs">
                    {c.signed_at ? (
                      <div>
                        <div>{new Date(c.signed_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
                        <div className="text-slate-500">IP: {c.signer_ip}</div>
                      </div>
                    ) : '—'}
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="ghost" onClick={() => copyLink(c)} title="Copia link firma" data-testid={`copy-link-${c.id}`}>
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => downloadPdf(c, false)} title="Scarica originale">
                        <Download className="w-3.5 h-3.5" />
                      </Button>
                      {c.status === 'signed' && (
                        <Button size="sm" onClick={() => downloadPdf(c, true)} className="bg-emerald-600 hover:bg-emerald-700" title="Scarica PDF firmato" data-testid={`download-signed-${c.id}`}>
                          <Download className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(c.id)} data-testid={`delete-contract-${c.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nuovo contratto da firmare</DialogTitle>
            <DialogDescription>Carica il PDF e indica l'email del cliente. Sarà inviato un link unico per la firma.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Titolo / Oggetto contratto</Label>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Contratto di consulenza sicurezza" data-testid="contract-title" />
            </div>
            <div>
              <Label>Email cliente *</Label>
              <Input type="email" value={form.client_email} onChange={e => setForm({ ...form, client_email: e.target.value })} placeholder="cliente@azienda.it" data-testid="contract-client-email" />
            </div>
            <div>
              <Label>Nome cliente / ragione sociale</Label>
              <Input value={form.client_name} onChange={e => setForm({ ...form, client_name: e.target.value })} placeholder="Mario Rossi / Eurobon SRL" data-testid="contract-client-name" />
            </div>
            <div>
              <Label>PDF contratto * (max 10 MB)</Label>
              <Input type="file" accept=".pdf" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="contract-file" />
            </div>
            <div>
              <Label>Note interne (non visibili al cliente)</Label>
              <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} />
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs text-blue-900 flex items-start gap-2">
              <Mail className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>Quando salvi, viene inviata un&apos;email al cliente con il link sicuro (valido 30 giorni).</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenCreate(false)}>Annulla</Button>
            <Button onClick={submit} disabled={submitting} data-testid="contract-submit">
              {submitting ? 'Invio…' : 'Crea e invia email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
